import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { FaExternalLinkAlt } from 'react-icons/fa';

// Styled Components
const RoutesSection = styled.section`
  color: ${({ theme }) => theme.text};
  text-align: center;
  padding: 100px 20px;
  
  h2 {
    color: ${({ theme }) => theme.sectionTitle};
  }
  h3 {
    padding-top: 20px;
    color: ${({ theme }) => theme.sectionSubTitle};
  }
  p {
    margin-top: 8px;
  }
`;

const SelectionsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 20px;
  padding: 20px;
  padding-top: 0px;
  padding-bottom: 10px;
  margin-top: 20px;
  height: 60px;
`;

const SelectionCard = styled.div`
  background-color: ${({ theme }) => theme.tagBackground};
  color: ${({ theme }) => theme.text};
  padding: 12px 24px;
  border: none;
  cursor: pointer;
  display: inline-block;
  transition: all 0.3s ease;
  border-radius: 0;
  text-decoration: none;
  box-shadow: 0 10px 20px rgba(0, 0, 0, 0.15);

  ${({ isSelected, theme }) =>
    isSelected &&
    `
    border-bottom: 3px solid #b63f3f;
  `}

  &:hover {
    border-bottom: 3px solid ${({ theme }) => theme.titleText};
    background-color: ${({ theme }) => theme.tagBackground};
    color: ${({ theme }) => theme.text};
    text-decoration: none;
  }

  &:focus {
    outline: none;
  }
`;

const RouteTags = styled.div`
  display: block;
  margin-top: 10px;
  margin-bottom: 30px;
  width: 100%;
  padding: 0px 20px;
`;

const RouteTag = styled.span`
  display: block;
  background-color: ${({ theme }) => theme.projectCard};
  color: ${({ theme }) => theme.tagText};
  padding: 5px 20px;
  border-radius: 0px;
  font-size: 14px;
  width: 100%; 
  box-shadow: 0 10px 20px rgba(0, 0, 0, 0.15);
  font-family: Verdana, Geneva, Tahoma, sans-serif;
`;

const DropdownsContainer = styled.div`
  display: flex;
  justify-content: center;
  gap: 20px;
  margin-top: 20px;
  padding: 10px 20px;
`;

const Dropdown = styled.select`
  background-color: ${({ theme }) => theme.tagBackground};
  color: ${({ theme }) => theme.text};
  padding: 12px;
  border-radius: 0px;
  font-size: 16px;
  cursor: pointer;
  width: 50%;
  max-width: 300px;

  option {
    background-color: ${({ theme }) => theme.tagBackground};
    color: ${({ theme }) => theme.text};
  }
`;

// Component
const Routes = () => {
  const [routesData, setRoutesData] = useState({});
  const [selectedCountry, setSelectedCountry] = useState('');
  const [selectedAirport, setSelectedAirport] = useState('');
  const [selectedDestination, setSelectedDestination] = useState('');
  const [route, setRoute] = useState('');

  // Lookup for destination ICAO codes
  const destinationICAO = {
    Rovaniemi: 'EFRO',
    Kiruna: 'ESNQ',
    Tromsø: 'ENTC'
  };

  useEffect(() => {
    fetch('../data/routes.json')
      .then(response => response.json())
      .then(data => setRoutesData(data))
      .catch(error => console.error('Error fetching routes data:', error));
  }, []);

  useEffect(() => {
    if (selectedCountry && selectedAirport && selectedDestination) {
      const airportRoutes = routesData[selectedCountry]?.[selectedAirport];
      if (airportRoutes) {
        setRoute(airportRoutes[selectedDestination] || '');
      }
    }
  }, [selectedCountry, selectedAirport, selectedDestination, routesData]);

  const handleCountryChange = (e) => {
    setSelectedCountry(e.target.value);
    setSelectedAirport(''); // Reset airport selection when country changes
  };

  const handleAirportChange = (e) => {
    setSelectedAirport(e.target.value);
  };

  const handleDestinationChange = (e) => {
    setSelectedDestination(e.target.value);
  };

  const destinations = ['Rovaniemi', 'Kiruna', 'Tromsø'];

  const renderDropdownOptions = (data) => {
    return Object.keys(data).map(key => <option key={key} value={key}>{key}</option>);
  };

  const airports = selectedCountry ? Object.keys(routesData[selectedCountry] || {}) : [];

  // Update the routesUrl to include departure and arrival ICAO codes
  const routesUrl = route && selectedAirport && selectedDestination
    ? `https://skyvector.com/?fpl=${selectedAirport} ${route.replace(/\s+/g, '%20')} ${destinationICAO[selectedDestination]}`
    : '';

  return (
    <RoutesSection id="routes">
      <h2>Routes</h2>
      <p>To help us better facilitate traffic flow, we recommend using our route planner.</p>
      <h3>Select your destination</h3>

      <SelectionsGrid>
        {destinations.map(destination => (
          <SelectionCard
            key={destination}
            isSelected={selectedDestination === destination}
            onClick={() => setSelectedDestination(destination)}
          >
            {destination}
          </SelectionCard>
        ))}
      </SelectionsGrid>

      {selectedDestination && (
        <>
          {/* Conditionally render content based on the selected destination */}
          {selectedDestination === 'Rovaniemi' ? (
            <>
              <h3>Select your origin</h3>

              <DropdownsContainer>
                <Dropdown 
                  value={selectedCountry} 
                  onChange={handleCountryChange}
                >
                  <option value="">Select Country</option>
                  {Object.keys(routesData).map(country => (
                    <option key={country} value={country}>{country}</option>
                  ))}
                </Dropdown>

                <Dropdown 
                  value={selectedAirport} 
                  onChange={handleAirportChange}
                  disabled={!selectedCountry}
                >
                  <option value="">Select Airport</option>
                  {selectedCountry && Object.keys(routesData[selectedCountry]).map(airport => (
                    <option key={airport} value={airport}>{airport}</option>
                  ))}
                </Dropdown>
              </DropdownsContainer>

              <p>If your airport or country is not listed, don't worry!</p>
              <p>You may check the mandatory routes from <a href="https://fss-map.lusep.fi/">fss-map.lusep.fi</a></p>

              {selectedCountry && selectedAirport && (
                <>
                  <h3>Your route to {selectedDestination}</h3>
                  <RouteTags>
                    <RouteTag>{route}</RouteTag>
                  </RouteTags>

                  <a target='_blank' href={routesUrl}>
                    Open Route in SkyVector <FaExternalLinkAlt style={{ fontSize: '14px' }} />
                  </a>
                </>
              )}
            </>
          ) : selectedDestination === 'Kiruna' ? (
            <>
              <h3>Mandatory routes to Kiruna</h3>

              <p>From EFRO</p>
              <RouteTags>
                <RouteTag>IBSAN DCT NEBET DCT TERPA</RouteTag>
              </RouteTags>

              <p>From ENTC</p>
              <RouteTags>
                <RouteTag>MOKAX DCT POBEL DCT LAPLA</RouteTag>
              </RouteTags>

              <p>From ESSA</p>
              <RouteTags>
                <RouteTag>RESNA T317 VAGAS</RouteTag>
              </RouteTags>

              <p>Other origins</p>
              <RouteTags>
                <RouteTag>Free routing (pilots may expect a re-route or vectoring if necessary)</RouteTag>
              </RouteTags>

              <p>You may also view the routes in <a href="https://fss-map.lusep.fi/">fss-map.lusep.fi</a></p>

            </>
          ) : selectedDestination === 'Tromsø' ? (
            <>
              <h3>Mandatory routes to Tromsø</h3>

              <p>From EFRO</p>
              <RouteTags>
                <RouteTag>IBSAN DCT AMULU DCT GAPRO DCT KIIKA</RouteTag>
              </RouteTags>

              <p>From ESNQ</p>
              <RouteTags>
                <RouteTag>RIXEM DCT MANKI</RouteTag>
              </RouteTags>

              <p>From ENGM</p>
              <RouteTags>
                <RouteTag>RIBBE Z125 SOMUB Z103 BNN DCT BDO Z200 BARVI</RouteTag>
              </RouteTags>

              <p>From South</p>
              <RouteTags>
                <RouteTag>... DCT BNN DCT BDO Z200 BARVI</RouteTag>
              </RouteTags>

              <p>From Sweden</p>
              <RouteTags>
                <RouteTag>... DCT RIXEM DCT MANKI</RouteTag>
              </RouteTags>

              <p>You may also view the routes in <a href="https://fss-map.lusep.fi/">fss-map.lusep.fi</a></p>
            </>
          ) : null}
        </>
      )}

    </RoutesSection>
  );
};

export default Routes;
